import React from 'react';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { Badge } from '@/dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import Dash from '@/dashboard/components/Dash';
import { formatPrice } from '@/dashboard/services/formatting';
import { getPaymentMethodLabel } from '@repo/lib';

function getStatusAppearance(status) {
  switch (status) {
    case 'refunded':
    case 'partially_refunded':
    case 'paid':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'pending':
      return 'warning';
    default:
      return 'info';
  }
}

function getTransactionType(transaction) {
  switch (transaction.status) {
    case 'refunded':
      return 'Refund';
    case 'partially_refunded':
      return 'Partial refund';
    case 'partially_paid':
      return 'Partial payment';
    default:
      return 'Payment';
  }
}

export function getTransactionColumns({ hasRefundedTransactions, onViewReason } = {}) {
  const columns = [
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => getTransactionType(row.original),
      enableSorting: false,
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) =>
        formatPrice({ amount: row.original.amount, currency: row.original.currency }),
      enableSorting: false,
    },
    {
      accessorKey: 'gateway',
      header: 'Payment gateway',
      cell: ({ row }) =>
        row.original.method && row.original.method?.gateway ? (
          upperFirst(row.original.method?.gateway)
        ) : (
          <Dash />
        ),
      enableSorting: false,
    },
    {
      accessorKey: 'method',
      header: 'Payment method',
      cell: ({ row }) =>
        row.original.method ? getPaymentMethodLabel(row.original.method) : <Dash />,
      enableSorting: false,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created at',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'd MMM, yyyy, HH:mm:ss'),
      enableSorting: false,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={getStatusAppearance(status)}>
            {status === 'refunded' || status === 'partially_refunded' || status === 'paid'
              ? 'Succeeded'
              : upperFirst(status.toLowerCase())}
          </Badge>
        );
      },
      enableSorting: false,
    },
  ];

  if (hasRefundedTransactions) {
    columns.push({
      id: 'reason',
      header: '',
      cell: ({ row }) => {
        const transaction = row.original;
        if (transaction.status !== 'refunded' && transaction.status !== 'partially_refunded') {
          return null;
        }
        return (
          <Button variant="outline" size="sm" onClick={() => onViewReason?.(transaction)}>
            View reason
          </Button>
        );
      },
      enableSorting: false,
      meta: { className: 'text-right' },
    });
  }

  return columns;
}
